import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { HistoryStore, ObjectHistory, TimeBucketUnits } from '@iotv/datamodel';
import { FEATURE_ON_OFF } from '@iotv/iotv-v3-types';
import { Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useContext, useEffect, useState } from 'react';
import { HistoryStoreContext } from '../../../ContextProviders/HistoryStoreContext';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { useBucketing } from '../../../hooks/useBucketing';
import { ObjectViewType } from '../../../types';
import { useColumnVisibilityForDataGrid, } from '../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { fontSize } from '../../NetworkSystem/cellFormatters/fontSize';
import { StripedDataGrid } from '../../factories/DataGrid/styles/striped';
import { SimpleIOModal } from '../../io/VOBbased/SimpleIOModal';
import { IOType } from '../../io/types';
import { assembleItems, defaultReadingKeyToPresentableNameMap, filterKeysByTypeToViewDefinition, filterKeysToViewDefinition, generateCol, } from './subFns';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Presents readings for a provided entity
 *
 * **Please wrap this component in a {@link HistoryStoreContext | `HistoryStoreContext`}!**
 *
 * @param context - Has various pieces of context for the entity
 * @param context.mostProximateCustomer - Closest customer in tree to the entity. Used to update live from the relevant mqtt topic.
 * @param context.contextObject - The entity to get readings for
 * @param context.historyStore - Unused, please wrap this component in a {@link HistoryStoreContext | `HistoryStoreContext`} instead!
 * @param context.historyStoreKey - The object history to get from the historyStore. Defaults to 'recentStateHistory'
 * @param filterMetricKeys - A list of keys to be displayed. If blank, all keys are used. Some keys are displayed regardless, but will only be exported if you specify them in filterMetricKeys (that blows!)
 * @param title - Title to display over the table
 * @param seeExport - Shows export control if true
 * @param seeImport - Shows import control if true
 * @param seeDelete - Shows control that allows one to destroy the object history if true
 * @param seeDeviceMetrics - Adds columns showing things like RSSI, SNR and payload if true
 * @param readingKeyToPresentableNamesMap - a mapping of object history key -> string to be displayed as column head.
 * @returns a datagrid with object history readings
 */
export const EntityReadingDataGrid = (props) => {
    'use no memo';
    // Modifying a value returned from a hook is not allowed. Consider moving the modification into the hook where the value is constructed.
    // `pushStateSnapShot` cannot be modified
    const { context, filterMetricKeys, title, seeImport, seeExport, seeDelete, seeDeviceMetrics, readingKeyToPresentableNamesMap = defaultReadingKeyToPresentableNameMap, } = props;
    /** 2024 11, currently the optional historyStoreIn (from ObjectCard) is not used, would require changes to avoid requerying in useHistoryStore and useObjectHistory) */
    const { contextObject, historyStoreKey = 'recentStateHistory' } = context;
    const { controls: { deleteControl, filterWidgetComponents, timeSelectorComponents }, historyStore: providedHistoryStore, historyStoreFns: { pushStateSnapShot }, objectHistory, loadingObjectHistory, } = useContext(HistoryStoreContext);
    const storeImportedHistoryStore = async (objects) => {
        // trim VOB-related things
        const states = objects.map((o) => {
            const { id, type, ...rest } = o;
            return rest;
        });
        // reformat timestamp and strip time string
        if (!Object.keys(states[0]).includes('timestamp') || !Object.keys(states[0]).includes('time'))
            return;
        const updates = states.map((u) => {
            const { timestamp, time, ...state } = u;
            return {
                timestamp,
                state,
            };
        });
        // create / duplicate object history
        const oh = new ObjectHistory(objectHistory ?? contextObject);
        // create / duplicate history store
        const hs = new HistoryStore(oh[historyStoreKey] ?? historyStore);
        for (const update of updates) {
            // update history store
            hs.push(update, true);
            // update UI
            pushStateSnapShot(update);
        }
        // apply update to object history
        oh[historyStoreKey] = hs;
        // store updated object history
        const { err, data } = await ConsumerDao.saveObjectHistory({ sk: contextObject.sk, objectHistory: oh });
        debug && console.log('Import object history store res', err, data);
    };
    const { components: { bucketWidth }, historyStore, } = useBucketing({
        bucketingOnOff: FEATURE_ON_OFF.ON,
        historyStore: providedHistoryStore,
        timeBucketDef: { unit: TimeBucketUnits.HOUR, width: 0 },
    });
    const historyStoreLastTimestamp = historyStore?.lastTimestamp();
    debug && console.log('historyStoreLastTimestamp', historyStoreLastTimestamp);
    useEffect(() => {
        debug && console.log('re rendering on changed matchedPrimary');
    }, [contextObject.sk, historyStoreLastTimestamp]);
    // Assemble items
    const metricItems = assembleItems({ contextObject, historyStore });
    // debug && console.log('EntityReadingDataGrid metrics', metricItems)
    const viewDefinition = filterMetricKeys
        ? filterKeysToViewDefinition(filterMetricKeys)
        : historyStore
            ? filterKeysByTypeToViewDefinition(historyStore?.keys(), historyStore)
            : {};
    const columnVisibilityReferences = {
        dataTableReference: 'EntityReadingDataGrid',
        objectTypeId: contextObject.type,
        viewType: ObjectViewType.List,
        externalColumnVisibility: undefined,
    };
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid(columnVisibilityReferences);
    debug && console.log('History Store ', { keys: historyStore?.keys(), columnVisibilityModel });
    const rowToGridRow = (item) => item;
    const columnMap = generateCol(viewDefinition, seeDeviceMetrics);
    const rows = metricItems.map((metricItem) => rowToGridRow(metricItem));
    const dataGridProps = {
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        columns: Object.entries(columnMap).map(([k, v]) => ({
            field: k,
            ...v,
            headerName: readingKeyToPresentableNamesMap?.[k] ?? v?.headerName ?? k,
        })),
        disableColumnSorting: true,
        loading: loadingObjectHistory,
        rows,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        density: 'compact',
        rowHeight: 32,
    };
    const locales = ['en', 'en-NZ', 'en-gb', 'zh-cn', 'de'];
    const [locale, setLocale] = useState('en-NZ');
    const extraControls = seeImport || seeExport || seeDelete || process.env.REACT_APP_IS_SUDO ? (_jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [(seeImport || process.env.REACT_APP_IS_SUDO) && (_jsx(SimpleIOModal, { columnIndexedKeys: Object.keys(viewDefinition),
                plainObjects: metricItems,
                initialIOType: IOType.IMPORT,
                allowIOTypeToggle: false,
                setPlainObjectsInParent: storeImportedHistoryStore,
                keyFilter: filterMetricKeys ? ['timestamp', 'time', ...filterMetricKeys] : undefined })), (seeExport || process.env.REACT_APP_IS_SUDO) && historyStore && (_jsx(SimpleIOModal, { columnIndexedKeys: Object.keys(viewDefinition),
                plainObjects: metricItems,
                initialIOType: IOType.EXPORT,
                allowIOTypeToggle: false,
                allowCreate: true,
                setPlainObjectsInParent: () => { } })), (seeDelete || process.env.REACT_APP_IS_SUDO) && historyStore && deleteControl] })) : (_jsx(_Fragment, {}));
    return (_jsxs(Stack, { direction: "column", paddingBlockStart: 1, gap: 1, children: [title && _jsx(Typography, { children: title }), _jsxs(Stack, { sx: { display: { xs: 'none', sm: 'flex' } }, direction: "row", alignItems: "center", gap: 1, children: [filterWidgetComponents.readings, timeSelectorComponents.shiftEarlierBtn, _jsxs(LocalizationProvider, { dateAdapter: AdapterDayjs, adapterLocale: locale, children: [_jsx(Stack, { style: { width: 210 }, children: timeSelectorComponents.fromPicker }), _jsx(Stack, { style: { width: 210 }, children: timeSelectorComponents.toPicker })] }), timeSelectorComponents.shiftLaterBtn, timeSelectorComponents.resetToNowBtn, timeSelectorComponents.periodField, timeSelectorComponents.confirmButton, bucketWidth, filterWidgetComponents.sample, extraControls] }), _jsxs(Stack, { sx: { display: { xs: 'flex', sm: 'none' } }, gap: 1, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", gap: 1, children: [filterWidgetComponents.readings, bucketWidth, filterWidgetComponents.sample] }), _jsx(Stack, { direction: "row", gap: 1, children: _jsxs(LocalizationProvider, { dateAdapter: AdapterDayjs, adapterLocale: locale, children: [timeSelectorComponents.fromPicker, timeSelectorComponents.toPicker] }) }), _jsxs(Stack, { direction: "row", justifyContent: "center", children: [timeSelectorComponents.shiftEarlierBtn, timeSelectorComponents.periodField, timeSelectorComponents.shiftLaterBtn, timeSelectorComponents.confirmButton] }), extraControls] }), _jsx(StripedDataGrid, { style: { fontSize }, ...dataGridProps })] }));
};
